import { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import { QRCodeSVG } from "qrcode.react";
import { api, getMac, setMac, setDeviceKey, getDeviceKey, proxied, FALLBACKS } from "@/lib/api";
import {
  Home, Tv, Film, Clapperboard, Heart, Search as SearchIcon, Settings as SettingsIcon,
  MonitorPlay, Play, Pause, RotateCcw, RotateCw, ArrowLeft, Maximize2, Loader2, Lock, Star, Calendar, History,
  CreditCard, LifeBuoy, Smartphone
} from "lucide-react";

// Reusable QR + link block driven by Admin-configured public URLs (loaded from backend).
function DeviceLinks({ pub, compact }) {
  const manage = pub?.manage_url?.trim();
  const pay = pub?.payment_url?.trim();
  const support = pub?.support_url?.trim();
  if (!manage && !pay && !support) return null;
  const Card = ({ icon: Icon, label, url, color }) => (
    <div className="flex flex-col items-center gap-2" data-testid={`devicelink-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="bg-white p-2.5 rounded-xl"><QRCodeSVG value={url} size={compact ? 92 : 128} /></div>
      <div className="flex items-center gap-1.5 text-sm font-semibold"><Icon className={`w-4 h-4 ${color}`} /> {label}</div>
      <span className="text-xs text-zinc-500 font-mono-data break-all max-w-[180px] text-center">{url.replace(/^https?:\/\//, "")}</span>
    </div>
  );
  return (
    <div className="flex flex-wrap items-start justify-center gap-8 mt-8" data-testid="device-links">
      {manage && <Card icon={Smartphone} label="Manage / Add playlist" url={manage} color="text-sky-400" />}
      {pay && <Card icon={CreditCard} label="Activate / Pay" url={pay} color="text-emerald-400" />}
      {support && <Card icon={LifeBuoy} label="Support" url={support} color="text-amber-400" />}
    </div>
  );
}

/* ---------------- Spatial (D-pad) navigation ---------------- */
function focusablesIn(root) {
  return [...(root || document).querySelectorAll("[data-focusable]")]
    .filter((el) => el.offsetParent !== null && !el.getAttribute("aria-disabled"));
}
function moveFocus(dir) {
  const modal = document.querySelector("[data-modal]");
  const els = focusablesIn(modal || document);
  if (!els.length) return;
  const cur = document.activeElement;
  if (!cur || !cur.hasAttribute || !cur.hasAttribute("data-focusable")) { els[0].focus(); return; }
  const r = cur.getBoundingClientRect();
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  let best = null, bestScore = Infinity;
  for (const el of els) {
    if (el === cur) continue;
    const b = el.getBoundingClientRect();
    const bx = b.left + b.width / 2, by = b.top + b.height / 2;
    const dx = bx - cx, dy = by - cy;
    let valid = false, primary = 0, cross = 0;
    if (dir === "left") { valid = dx < -6; primary = -dx; cross = Math.abs(dy); }
    else if (dir === "right") { valid = dx > 6; primary = dx; cross = Math.abs(dy); }
    else if (dir === "up") { valid = dy < -6; primary = -dy; cross = Math.abs(dx); }
    else if (dir === "down") { valid = dy > 6; primary = dy; cross = Math.abs(dx); }
    if (!valid) continue;
    const score = primary + cross * 2.5;
    if (score < bestScore) { bestScore = score; best = el; }
  }
  if (best) { best.focus(); best.scrollIntoView({ block: "nearest", inline: "nearest" }); }
}

/* ---------------- TV video player with remote controls ---------------- */
function TvVideoPlayer({ url, poster, live, hideBack, controls = true, resumeFrom = 0, onProgress, onBack, paused = false, controlsVisible = true, showFullscreen = true, forceProxy = false, onMode }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const lastSaved = useRef(0);
  const progRef = useRef(onProgress); progRef.current = onProgress;
  const hlsRef = useRef(null);
  const srcRef = useRef("");
  const recRef = useRef(0);
  const resumeRef = useRef(resumeFrom); resumeRef.current = resumeFrom;
  // Direct-first playback refs
  const urlRef = useRef(url);
  const forceProxyRef = useRef(forceProxy); forceProxyRef.current = forceProxy;
  const modeRef = useRef("proxy");
  const triedProxyRef = useRef(false);
  const bufferedOkRef = useRef(false);
  const directWatchRef = useRef(null);
  const fallbackRef = useRef(null);
  const modeReportedRef = useRef(false);
  const onModeRef = useRef(onMode); onModeRef.current = onMode;

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const proxify = (u) => `${BACKEND}/api/stream?url=${encodeURIComponent(u)}`;
  const isWebView = typeof window !== "undefined" && !!window.AndroidTV;
  const pageHttps = typeof window !== "undefined" && window.location?.protocol === "https:";

useEffect(() => {
  try {
    const v = document.createElement("video");
    const nativeHls = !!(v.canPlayType("application/vnd.apple.mpegurl") || v.canPlayType("application/x-mpegURL"));
    api.post(`/device/${mac}/log`, {
      type: "playback_diag",
      message: `hlsjs=${Hls.isSupported()} androidtv=${typeof window !== "undefined" && !!window.AndroidTV} nativehls=${nativeHls}`,
      app_version: (typeof window !== "undefined" && window.AndroidTV?.versionName) ? window.AndroidTV.versionName() : ""
    }).catch(() => {});
  } catch (e) {}
}, [mac]);
  // Decide if a stream may be played directly from the provider (skips our server).
  const canDirect = (u) => {
    if (forceProxyRef.current || !u) return false;
    // Browsers block http media on an https page (mixed content); native WebView allows it.
    if (!isWebView && pageHttps && u.startsWith("http://")) return false;
    return true;
  };
  const reportMode = () => {
    if (modeReportedRef.current) return;
    modeReportedRef.current = true;
    try { onModeRef.current?.(modeRef.current === "direct" ? "direct" : "proxied"); } catch (e) {}
  };

  const isHlsUrl = (u) => u.includes(".m3u8") || u.includes("/live/") || !/\.(mp4|mkv|avi|mov|webm|ts)(\?|$)/i.test(u);
  const zapLog = (stage) => { if (!window.__zapDebug) return; const t0 = window.__zapStart; if (t0) console.log(`[zap] ${stage} +${Math.round(performance.now() - t0)}ms`); };

  // One-time setup: video listeners, persistent HLS instance (reused across channel changes), stall watchdog.
  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    let watchdog, recTimer;
    const report = () => { if (progRef.current && v.currentTime > 0) progRef.current(v.currentTime, isFinite(v.duration) ? v.duration : 0); };
    const onPlay = () => { setLoading(false); setPlaying(true); zapLog("playing"); };
    const onPause = () => setPlaying(false);
    const onLoadedData = () => { setLoading(false); zapLog("loadeddata"); };
    const meta = () => { if (resumeRef.current > 0 && isFinite(v.duration)) { try { v.currentTime = resumeRef.current; } catch (e) {} } };
    const time = () => { setCur(v.currentTime); setDur(v.duration || 0); if (v.currentTime - lastSaved.current >= 10) { lastSaved.current = v.currentTime; report(); } };
    const onNativeErr = () => { if (!hlsRef.current) { if (fallbackRef.current && fallbackRef.current()) return; setError(true); setLoading(false); } };
    v.addEventListener("playing", onPlay); v.addEventListener("pause", onPause);
    v.addEventListener("loadeddata", onLoadedData); v.addEventListener("loadedmetadata", meta);
    v.addEventListener("timeupdate", time); v.addEventListener("ended", report); v.addEventListener("error", onNativeErr);

    const recover = (hard) => {
      const hls = hlsRef.current; if (!hls) return;
      if (recRef.current >= 8) { setError(true); setLoading(false); return; }
      recRef.current++; setLoading(true);
      clearTimeout(recTimer);
      recTimer = setTimeout(() => { try { if (hard) { hls.stopLoad(); hls.loadSource(srcRef.current); } hls.startLoad(); } catch (e) {} }, Math.min(1000 * recRef.current, 6000));
    };

    // Quick, automatic fallback: if a DIRECT attempt fails, immediately retry via the
    // proxy (once). No long timeout — keeps zapping snappy.
    const fallbackToProxy = () => {
      if (modeRef.current !== "direct" || triedProxyRef.current) return false;
      triedProxyRef.current = true;
      modeRef.current = "proxy";
      clearTimeout(directWatchRef.current);
      const psrc = proxify(urlRef.current);
      srcRef.current = psrc; recRef.current = 0;
      setError(false); setLoading(true);
      zapLog("fallback_to_proxy");
      try {
        if (hlsRef.current) { hlsRef.current.stopLoad(); hlsRef.current.loadSource(psrc); hlsRef.current.startLoad(); }
        else { const v2 = videoRef.current; if (v2) { v2.src = psrc; v2.play().catch(() => {}); } }
      } catch (e) {}
      return true;
    };
    fallbackRef.current = fallbackToProxy;

    if (isHlsUrl(url) && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true, lowLatencyMode: true, startFragPrefetch: true, liveDurationInfinity: true,
        backBufferLength: 30, maxBufferLength: 8, maxMaxBufferLength: 30,
        manifestLoadingTimeOut: 20000, manifestLoadingMaxRetry: 6, manifestLoadingRetryDelay: 800, manifestLoadingMaxRetryTimeout: 64000,
        levelLoadingTimeOut: 20000, levelLoadingMaxRetry: 6, levelLoadingRetryDelay: 800, levelLoadingMaxRetryTimeout: 64000,
        fragLoadingTimeOut: 30000, fragLoadingMaxRetry: 8, fragLoadingRetryDelay: 800, fragLoadingMaxRetryTimeout: 64000,
      });
      hlsRef.current = hls;
      hls.attachMedia(v);
      hls.on(Hls.Events.MANIFEST_LOADED, () => zapLog("manifest_loaded"));
      hls.on(Hls.Events.MANIFEST_PARSED, () => { zapLog("manifest_parsed"); v.play().catch(() => {}); });
      hls.on(Hls.Events.FRAG_LOADED, () => zapLog("frag_loaded"));
      hls.on(Hls.Events.FRAG_BUFFERED, () => { recRef.current = 0; setError(false); bufferedOkRef.current = true; clearTimeout(directWatchRef.current); reportMode(); });
      hls.on(Hls.Events.ERROR, (_e, d) => {
        if (!d.fatal) return;
        if (fallbackToProxy()) return; // direct failed → straight to proxy
        if (d.type === Hls.ErrorTypes.MEDIA_ERROR) { try { hls.recoverMediaError(); } catch (e) { recover(true); } return; }
        if (d.type === Hls.ErrorTypes.NETWORK_ERROR) { recover(recRef.current >= 2); return; }
        recover(true);
      });
    }

    let lastT = -1, stalls = 0;
    watchdog = setInterval(() => {
      if (!v || v.paused || v.ended || v.seeking || v.readyState < 2) return;
      if (v.currentTime === lastT) { stalls++; if (stalls >= 2) setLoading(true); if (stalls >= 3) { stalls = 0; recover(true); } }
      else { lastT = v.currentTime; if (stalls) { stalls = 0; setLoading(false); } }
    }, 3000);
    if (controls) setTimeout(() => { const b = wrapRef.current?.querySelector("[data-focusable]"); b && b.focus(); }, 250);

    return () => {
      report(); clearInterval(watchdog); clearTimeout(recTimer);
      v.removeEventListener("playing", onPlay); v.removeEventListener("pause", onPause);
      v.removeEventListener("loadeddata", onLoadedData); v.removeEventListener("loadedmetadata", meta);
      v.removeEventListener("timeupdate", time); v.removeEventListener("ended", report); v.removeEventListener("error", onNativeErr);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, []);

  // Load / swap source on url change — reuses the same HLS instance & video element (no detach = no play-button gap, fast zap).
  useEffect(() => {
    const v = videoRef.current; if (!v || !url) return;
    urlRef.current = url;

    triedProxyRef.current = false;
    bufferedOkRef.current = false;
    modeReportedRef.current = false;
    clearTimeout(directWatchRef.current);
    const direct = canDirect(url);
    modeRef.current = direct ? "direct" : "proxy";
    const src = direct ? url : proxify(url);
    srcRef.current = src; recRef.current = 0; lastSaved.current = 0;
    setError(false); setLoading(true);
    zapLog(direct ? "url_effect_direct" : "url_effect_proxy");
    if (hlsRef.current) { zapLog("hls_loadSource"); hlsRef.current.loadSource(src); hlsRef.current.startLoad(); }
    else { v.src = src; v.play().catch(() => {}); }
    if (!direct) { reportMode(); } // proxied from the start
    else {
      // Backup for silent direct stalls (no error, no data): fall back quickly.
      directWatchRef.current = setTimeout(() => { if (!bufferedOkRef.current && fallbackRef.current) fallbackRef.current(); }, 3500);
    }
    return () => clearTimeout(directWatchRef.current);
  }, [url]);

  // Pause + stop loading when another player takes over (e.g. Catch-Up overlay),
  // so there is only ever ONE active audio/video source. Resume when released.
  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    if (paused) {
      try { v.pause(); } catch (e) {}
      try { hlsRef.current?.stopLoad(); } catch (e) {}
    } else {
      try { hlsRef.current?.startLoad(); } catch (e) {}
      v.play().catch(() => {});
    }
  }, [paused]);

  // When the controls overlay becomes visible again (auto-hide), move focus onto a
  // control so the D-pad has a target.
  useEffect(() => {
    if (controls && controlsVisible) {
      const tm = setTimeout(() => { wrapRef.current?.querySelector('[data-testid="tv-playpause-btn"]')?.focus(); }, 40);
      return () => clearTimeout(tm);
    }
  }, [controlsVisible]);

  const toggle = () => { const v = videoRef.current; if (!v) return; v.paused ? v.play() : v.pause(); };
  const seek = (s) => { const v = videoRef.current; if (v && isFinite(v.duration)) v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + s)); };
  const full = () => { const el = wrapRef.current; if (!document.fullscreenElement) el?.requestFullscreen?.(); else document.exitFullscreen?.(); };
  const t = (s) => { if (!isFinite(s)) return "0:00"; const m = Math.floor(s / 60), ss = Math.floor(s % 60); return `${m}:${ss.toString().padStart(2, "0")}`; };

  const btn = "flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-zinc-800/90 text-white text-lg font-semibold";
  return (
    <div ref={wrapRef} className="relative w-full h-full bg-black rounded-xl overflow-hidden">
      <video ref={videoRef} autoPlay playsInline poster={poster || undefined} className="w-full h-full object-contain bg-black" />
      {loading && !error && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Loader2 className="w-9 h-9 text-red-500 animate-spin" /></div>}
      {error && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/90 text-center px-8"><p className="text-zinc-300 text-xl max-w-lg">This channel/content cannot be played (possible CORS/geo-block on the source).</p></div>}
      {controls && controlsVisible && (
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/70 to-transparent">
        {!live && dur > 0 && (
          <div className="mb-3 flex items-center gap-3 text-sm text-zinc-300 font-mono-data">
            <span>{t(cur)}</span>
            <div className="flex-1 h-2 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-red-600" style={{ width: `${(cur / dur) * 100}%` }} /></div>
            <span>{t(dur)}</span>
          </div>
        )}
        <div className="flex items-center gap-4">
          <button data-testid="tv-back-btn" data-focusable onClick={onBack} className={`${btn} ${hideBack ? "hidden" : ""}`}><ArrowLeft className="w-6 h-6" /> Back</button>
          <button data-testid="tv-playpause-btn" data-focusable onClick={toggle} className={btn}>{playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />} {playing ? "Pause" : "Play"}</button>
          {!live && <button data-testid="tv-rew-btn" data-focusable onClick={() => seek(-10)} className={btn}><RotateCcw className="w-6 h-6" /> 10s</button>}
          {!live && <button data-testid="tv-fwd-btn" data-focusable onClick={() => seek(30)} className={btn}><RotateCw className="w-6 h-6" /> 30s</button>}
          {showFullscreen && <button data-testid="tv-full-btn" data-focusable onClick={full} className={btn}><Maximize2 className="w-6 h-6" /> Fullscreen</button>}
        </div>
      </div>
      )}
    </div>
  );
}

const NAV = [
  { key: "home", label: "Home", icon: Home },
  { key: "live", label: "Live TV", icon: Tv },
  { key: "movie", label: "Movies", icon: Film },
  { key: "series", label: "Series", icon: Clapperboard },
  { key: "search", label: "Search", icon: SearchIcon },
  { key: "favorites", label: "Favorites", icon: Heart },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function TvPlayer() {
  const [device, setDevice] = useState(null);
  const [mac, setMacS] = useState(getMac());
  const [section, setSection] = useState("home");
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // live inline
  const [epg, setEpg] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [episodes, setEpisodes] = useState(null);
  const [activeSeries, setActiveSeries] = useState(null);
  const [favIds, setFavIds] = useState([]);
  const [continueItems, setContinueItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showExit, setShowExit] = useState(false);
  const [fsLive, setFsLive] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [liveQuery, setLiveQuery] = useState("");
  const [showRecent, setShowRecent] = useState(false);
  const [update, setUpdate] = useState(null);
  const [pubSettings, setPubSettings] = useState(null);
  const [sessionPin, setSessionPin] = useState("");
  const [adultUnlocked, setAdultUnlocked] = useState(false);
  const [pinDialog, setPinDialog] = useState(null); // { catName, onSuccess }
  const lastFocus = useRef(null);
  const infoTimer = useRef(null);
  const [cuCtrl, setCuCtrl] = useState(true); // catch-up controls overlay visible
  const cuCtrlRef = useRef(true); cuCtrlRef.current = cuCtrl;
  const cuTimer = useRef(null);
  const pokeCu = () => {
    setCuCtrl(true);
    clearTimeout(cuTimer.current);
    cuTimer.current = setTimeout(() => setCuCtrl(false), 3000);
  };

  const requestUnlock = (catName, onSuccess) => {
    if (adultUnlocked && sessionPin) { onSuccess(); return; }
    lastFocus.current = document.activeElement?.getAttribute("data-testid") || null;
    setPinDialog({ catName, onSuccess });
  };

  // Adult content auto-locks the moment the user leaves the adult group (selects any
  // other/non-adult group or switches section). PIN must be re-entered next time.
  const ADULT_RE = /(xxx|adult|18\s*\+|\+\s*18|porn|eroti|hardcore|за возрасни|adulti|18plus)/i;
  const isAdultCat = (name) => !!name && name !== "all" && (cats.find((c) => c.name === name)?.adult || ADULT_RE.test(name));
  useEffect(() => {
    if (adultUnlocked && !isAdultCat(activeCat)) { setAdultUnlocked(false); setSessionPin(""); }
  }, [activeCat, section]); // eslint-disable-line

  // Catch-Up / Movies / Series controls auto-hide: show on start, hide after 3s of
  // inactivity. (Applies to any non-live overlay; Live TV is untouched.)
  useEffect(() => {
    if (playing && playing.kind !== "live") pokeCu();
    else { clearTimeout(cuTimer.current); setCuCtrl(true); }
    return () => clearTimeout(cuTimer.current);
  }, [playing]); // eslint-disable-line

  // register + load device
  useEffect(() => {
    (async () => {
      let m = getMac();

      if (window.AndroidTV && typeof window.AndroidTV.deviceId === "function") {
        const nativeId = window.AndroidTV.deviceId();
        if (nativeId) {
          const clean = nativeId.replace(/[^A-Fa-f0-9]/g, "").toUpperCase();
          if (clean.length >= 12) {
            m = clean.slice(0, 12).match(/.{1,2}/g).join(":");
            setMac(m);
          }
        }
      }

      if (!m) {
        throw new Error("No stable device ID available");
      }

      const { data } = await api.post("/device/register", { mac: m });
      setMac(data.mac); setDeviceKey(data.device_key); setMacS(data.mac); setDevice(data);
      api.get(`/device/${data.mac}/favorites`).then(({ data: f }) => setFavIds(f.items.map((i) => i.id))).catch(() => {});
      api.get(`/device/${data.mac}/continue`).then(({ data: c }) => setContinueItems(c.items)).catch(() => {});
      api.get(`/settings`).then(({ data: s }) => setPubSettings(s)).catch(() => {});
    })();
  }, []);

  const loadContinue = useCallback(() => { if (mac) api.get(`/device/${mac}/continue`).then(({ data }) => setContinueItems(data.items)).catch(() => {}); }, [mac]);

  // Heartbeat (presence) + app update check
  useEffect(() => {
    if (!mac) return;
    const build = window.AndroidTV?.versionCode?.() ?? window.__APP_BUILD ?? 0;
    const version = window.AndroidTV?.versionName?.() ?? window.__APP_VERSION ?? "web";
    const beat = () => api.post(`/device/${mac}/heartbeat`, { app_version: version, app_build: build }).catch(() => {});
    beat();
    const id = setInterval(beat, 45000);
    api.get(`/device/${mac}/app-update`).then(({ data }) => {
      if (data.version_code && Number(data.version_code) > Number(build) && data.apk_url) setUpdate(data);
    }).catch(() => {});
    api.post(`/device/${mac}/log`, { type: "app_opened", app_version: version }).catch(() => {});
    return () => clearInterval(id);
  }, [mac]);

  // load categories + items for content sections
  useEffect(() => {
    if (!mac || !device?.active) return;
    if (["movie", "series", "live"].includes(section)) {
      setActiveCat("all");
      api.get(`/device/${mac}/categories`, { params: { type: section } }).then(({ data }) => setCats(data)).catch(() => setCats([]));
    }
  }, [section, mac, device?.active]);

  useEffect(() => {
    if (!mac || !device?.active) return;
    const type = section;
    if (!["movie", "series", "live"].includes(type)) return;
    if (type === "live" && showRecent) { setLoading(false); return; }
    setLoading(true);
    const searching = type === "live" && liveQuery.trim().length >= 1;
    const t = setTimeout(() => {
      api.get(`/device/${mac}/content`, { params: { type, category: searching ? "all" : activeCat, search: searching ? liveQuery.trim() : undefined, pin: sessionPin || undefined, limit: 200 } })
        .then(({ data }) => setItems(data.items)).finally(() => setLoading(false));
    }, searching ? 250 : 150);
    return () => clearTimeout(t);
  }, [section, activeCat, mac, device?.active, liveQuery, showRecent, sessionPin]);

  useEffect(() => {
    if (section !== "favorites" || !mac) return;
    api.get(`/device/${mac}/favorites`).then(({ data }) => setItems(data.items));
  }, [section, mac]);

  // search
  useEffect(() => {
    if (section !== "search") return;
    if (search.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => {
      api.get(`/device/${mac}/search`, { params: { q: search } }).then(({ data }) => {
        setItems([...data.live, ...data.movie, ...data.series]);
      });
    }, 350);
    return () => clearTimeout(t);
  }, [search, section, mac]);

  const openPlayer = (item, resumeFrom = 0) => {
    lastFocus.current = document.activeElement?.getAttribute("data-testid") || null;

    api.post(`/device/${mac}/log`, {
      type: "native_gate",
      message: `section=${section} kind=${item?.kind} type=${item?.type} bridge=${!!window.AndroidTV} openPlayer=${typeof window.AndroidTV?.openPlayer} url=${item?.url || ""}`
    }).catch(() => {});

    const isLive = item?.kind === "live" || item?.type === "live";

    if (
      ["catchup", "movie", "episode"].includes(item?.type) &&
      window.AndroidTV &&
      typeof window.AndroidTV.openCatchupPlayer === "function" &&
      item?.url
    ) {
      window.AndroidTV.openCatchupPlayer(item.url);
      reportPlaybackMode("direct", item.name);
      setPlaying({ ...item, resumeFrom, nativeCatchup: true });
      return;
    }

    if (!isLive && window.AndroidTV && typeof window.AndroidTV.closePlayer === "function") {
      window.AndroidTV.closePlayer();
    }

    setPlaying({ ...item, resumeFrom });
  };
  const closePlayer = () => {
    setPlaying(null);
    loadContinue();
    setTimeout(() => { const id = lastFocus.current; const el = id && document.querySelector(`[data-testid="${CSS.escape(id)}"]`); (el || document.querySelector("[data-focusable]"))?.focus(); }, 60);
  };

  const saveProgress = (item, position, duration) => {
    if (!item?.id || item.kind === "live") return;
    api.post(`/device/${mac}/progress`, { content_id: item.id, name: item.name, logo: item.logo || "", type: item.type || "movie", url: item.url, position, duration }).catch(() => {});
  };

  // Report Direct vs Proxied playback for admin diagnostics (throttled).
  const lastModePost = useRef({ key: "", at: 0 });
  const activeModeRef = useRef("proxied");
  const sessionId = useRef(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const reportPlaybackMode = (mode, name) => {
    activeModeRef.current = mode === "direct" ? "direct" : "proxied";
    const key = `${mode}|${name || ""}`;
    const now = Date.now();
    if (lastModePost.current.key === key && now - lastModePost.current.at < 4000) return;
    lastModePost.current = { key, at: now };
    api.post(`/device/${mac}/playback-mode`, { mode, content_name: name || "" }).catch(() => {});
  };

  // Active-stream heartbeat → powers the admin "active now" counters. One session per
  // device; expires server-side ~20s after playback stops.
  useEffect(() => {
    const isActive = () => (!!playing) || (section === "live" && !!selected);
    const send = () => api.post(`/device/${mac}/stream-ping`, { session_id: sessionId.current, mode: activeModeRef.current, active: isActive() }).catch(() => {});
    send();
    const iv = setInterval(send, 8000);
    return () => clearInterval(iv);
  }, [playing, section, selected, mac]);

  const onCardEnter = async (item) => {
    if (item.type === "live") {
      if (section === "live") { setSelected(item); loadEpg(item.id); }
      else openPlayer({ ...item, kind: "live" });
      return;
    }
    if (item.type === "series") {
      setActiveSeries(item); setEpisodes("loading");
      const { data } = await api.get(`/device/${mac}/series/${item.id}`);
      if (!data.episodes.length) { if (item.url) openPlayer(item); else setEpisodes(null); }
      else setEpisodes(data.episodes);
      return;
    }
    openPlayer(item);
  };

  const loadEpg = async (id) => { setEpg([]); try { const { data } = await api.get(`/device/${mac}/epg/${id}`); setEpg(data.epg); } catch { setEpg([]); } };

  const reloadPlaylist = async () => {
    const pid = device.active_playlist || device.playlists?.[0]?.id;
    if (!pid) return;
    setLoading(true);
    try { await api.post(`/device/${mac}/playlist/${pid}/refresh`); const { data } = await api.get(`/device/${mac}`); setDevice(data); setSection("home"); }
    finally { setLoading(false); }
  };
  const switchPlaylist = async (pid) => { await api.post(`/device/${mac}/active-playlist/${pid}`); const { data } = await api.get(`/device/${mac}`); setDevice(data); setSelected(null); setSection("home"); };
  const reloadDevice = async () => { const { data } = await api.get(`/device/${mac}`); setDevice(data); };
  const exitApp = () => {
    // Native Android wrapper bridge (WebView addJavascriptInterface name "AndroidTV").
    try { if (window.AndroidTV && typeof window.AndroidTV.exitApp === "function") { window.AndroidTV.exitApp(); return; } } catch (e) {}
    let closed = false; try { window.close(); closed = window.closed; } catch (e) {}
    if (!closed) setShowExit(true);
  };

  // ---- Live TV fullscreen (remote-first) ----
  const showInfoBriefly = () => {
    setShowInfo(true);
    if (infoTimer.current) clearTimeout(infoTimer.current);
    infoTimer.current = setTimeout(() => setShowInfo(false), 2800);
  };
  const postRecent = (ch) => { if (mac && ch) { api.post(`/device/${mac}/recent`, { content_id: ch.id || ch.content_id, name: ch.name, logo: ch.logo, category: ch.category, url: ch.url }).catch(() => {}); api.post(`/device/${mac}/log`, { type: "playback_started", message: ch.name }).catch(() => {});
try {
  const v = document.createElement("video");
  const nativeHls = !!(v.canPlayType("application/vnd.apple.mpegurl") || v.canPlayType("application/x-mpegURL"));
  api.post(`/device/${mac}/log`, {
    type: "playback_diag",
    message: `hlsjs=${Hls.isSupported()} androidtv=${typeof window !== "undefined" && !!window.AndroidTV} nativehls=${nativeHls}`
  }).catch(() => {});
} catch (e) {} } };
  const changeChannel = (dir) => {
    if (!items.length || !selected) return;
    const idx = items.findIndex((c) => c.id === selected.id);
    if (idx < 0) return;
    let next = dir === "next" ? idx + 1 : idx - 1;
    if (next < 0) next = items.length - 1;
    if (next >= items.length) next = 0;
    const ch = items[next];
    window.__zapStart = performance.now(); if (window.__zapDebug) console.log("[zap] keypress(0ms)", ch.name);

    setSelected(ch);
    loadEpg(ch.id);
    showInfoBriefly();
    postRecent(ch);

    if (fsLive && window.AndroidTV && typeof window.AndroidTV.changePlayer === "function" && ch?.url) {
      window.AndroidTV.changePlayer(ch.url);
      reportPlaybackMode("direct", ch.name);
    }
  };
  const onChannelActivate = (ch) => {
    if (selected?.id === ch.id) {
      if (window.AndroidTV && typeof window.AndroidTV.fullscreenPlayer === "function") {
        window.AndroidTV.fullscreenPlayer();
      }
      setFsLive(true);
      showInfoBriefly();
      return;
    }

    window.__zapStart = performance.now();
    if (window.__zapDebug) console.log("[zap] select(0ms)", ch.name);

    setSelected(ch);
    loadEpg(ch.id);
    postRecent(ch);
  };

  useEffect(() => {
    if (!selected?.url || fsLive || playing) return;

    const t = setTimeout(() => {
      const preview = document.querySelector('[data-testid="tv-live-preview"]');
      if (!preview || !window.AndroidTV || typeof window.AndroidTV.openPlayer !== "function") return;

      const r = preview.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      window.AndroidTV.openPlayer(
        selected.url,
        Math.round(r.left * dpr),
        Math.round(r.top * dpr),
        Math.round(r.width * dpr),
        Math.round(r.height * dpr)
      );
      reportPlaybackMode("direct", selected.name);
    }, 100);

    return () => clearTimeout(t);
  }, [selected?.id, selected?.url, playing]);
  const exitFsLive = () => {
    setFsLive(false);
    setTimeout(() => {
      const el = selected?.id && document.querySelector(`[data-testid="tvchan-${CSS.escape(selected.id)}"]`);
      (el || document.querySelector("[data-focusable]"))?.focus();
    }, 60);
  };

  useEffect(() => {
    const onNativePlayerBack = () => exitFsLive();

    const onNativeCatchupBack = () => {
      setPlaying(null);
      loadContinue();

      setTimeout(() => {
        const el = selected?.id && document.querySelector(`[data-testid="tvchan-${CSS.escape(selected.id)}"]`);
        (el || document.querySelector("[data-focusable]"))?.focus();
      }, 60);
    };

    window.addEventListener("nativePlayerBack", onNativePlayerBack);
    window.addEventListener("nativeCatchupBack", onNativeCatchupBack);

    return () => {
      window.removeEventListener("nativePlayerBack", onNativePlayerBack);
      window.removeEventListener("nativeCatchupBack", onNativeCatchupBack);
    };
  }, [selected?.id]);

  const toggleFav = async (item) => {
    const { data } = await api.post(`/device/${mac}/favorites/${item.id}`);
    setFavIds((p) => data.favorite ? [...p, item.id] : p.filter((i) => i !== item.id));
    if (section === "favorites") api.get(`/device/${mac}/favorites`).then(({ data: f }) => setItems(f.items));
  };

  // Global D-pad handler
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      // Catch-Up / Movies / Series controls auto-hide: any key reveals them; if hidden
      // the key ONLY reveals (does not also trigger Back/Pause/etc). Live TV is untouched.
      if (playing && playing.kind !== "live") {
        const wasHidden = !cuCtrlRef.current;
        pokeCu();
        if (wasHidden) { e.preventDefault(); return; }
      }
      if (fsLive) {
        if (k === "ArrowDown") { e.preventDefault(); changeChannel("next"); }
        else if (k === "ArrowUp") { e.preventDefault(); changeChannel("prev"); }
        else if (k === "ArrowLeft" || k === "ArrowRight") { e.preventDefault(); }
        else if (k === "Enter") { e.preventDefault(); showInfoBriefly(); }
        else if (k === "Backspace" || k === "Escape" || k === "GoBack") { e.preventDefault(); exitFsLive(); }
        return;
      }
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(k)) {
        e.preventDefault();
        moveFocus(k.replace("Arrow", "").toLowerCase());
      } else if (k === "Enter") {
        const el = document.activeElement;
        if (el && el.tagName !== "INPUT") { e.preventDefault(); el.click(); }
      } else if (k === "Backspace" || k === "Escape" || k === "GoBack") {
        const ae = document.activeElement;
        if (ae && ae.tagName === "INPUT") return; // let text inputs handle their own keys
        e.preventDefault();
        if (pinDialog) setPinDialog(null);
        else if (showExit) setShowExit(false);
        else if (playing) closePlayer();
        else if (episodes) setEpisodes(null);
        else if (section !== "home") { setSelected(null); setSection("home"); setTimeout(() => document.querySelector('[data-testid="tvmenu-live"]')?.focus(), 60); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing, episodes, section, showExit, fsLive, selected, items, pinDialog]);

  // initial focus
  useEffect(() => { const t = setTimeout(() => { if (!document.querySelector("[data-focusable]:focus")) document.querySelector("[data-focusable]")?.focus(); }, 400); return () => clearTimeout(t); }, [device, section]);

  if (!device) return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><Loader2 className="w-12 h-12 text-red-500 animate-spin" /></div>;

  // Activation / trial-expired gate
  if (!device.active) return <ActivationScreen device={device} pub={pubSettings} />;

  const pl = device.playlists?.[0];
  const noPlaylist = !device.playlists?.length;

  return (
    <div className="tv-root h-screen overflow-hidden bg-[#050505] text-zinc-50">
      <main className="h-full overflow-hidden">
        {noPlaylist ? (
          <NoPlaylist mac={device.mac} deviceKey={device.device_key} pub={pubSettings} />
        ) : section === "home" ? (
          <TvMenu device={device} continueItems={continueItems} loading={loading}
            onGo={(k) => { setSelected(null); setSection(k); }} onReload={reloadPlaylist} onExit={exitApp}
            onResume={(it) => openPlayer({ id: it.content_id, name: it.name, url: it.url, logo: it.logo, type: it.type }, it.position)} />
        ) : section === "playlist" ? (
          <TvPlaylistPicker device={device} onSwitch={switchPlaylist} onBack={() => setSection("home")} />
        ) : section === "settings" ? (
          <SectionWrap title="Settings" onBack={() => setSection("home")}><TvSettings device={device} mac={mac} onReload={reloadDevice} pub={pubSettings} /></SectionWrap>
        ) : section === "search" ? (
          <SectionWrap title="Search" onBack={() => setSection("home")}><TvSearch search={search} setSearch={setSearch} items={items} onEnter={onCardEnter} favIds={favIds} onFav={toggleFav} /></SectionWrap>
        ) : section === "live" ? (
          <TvLive items={items} cats={cats} activeCat={activeCat} setActiveCat={setActiveCat} loading={loading}
            selected={selected} onActivate={onChannelActivate} epg={epg} fsLive={fsLive} showInfo={showInfo}
            liveQuery={liveQuery} setLiveQuery={setLiveQuery} showRecent={showRecent} setShowRecent={setShowRecent}
            recent={device.recent_channels || []} requestUnlock={requestUnlock} livePaused={!!playing}
            onMode={(m) => reportPlaybackMode(m, selected?.name)}
            onCatchup={(p) => openPlayer({ id: `catchup-${selected.id}-${p.start_ts}`, name: `${selected.name} — ${p.title}`, url: p.catchup_url, logo: selected.logo, type: "catchup" })}
            onFav={toggleFav} favIds={favIds} onBack={() => setSection("home")} />
        ) : (
          <TvBrowse title={section === "movie" ? "Movies" : section === "series" ? "Series" : "Favorites"} items={items} cats={cats} activeCat={activeCat}
            setActiveCat={setActiveCat} loading={loading} onEnter={onCardEnter} favIds={favIds} requestUnlock={requestUnlock} onBack={() => setSection("home")} />
        )}
      </main>

      {/* Adult / parental PIN dialog */}
      {pinDialog && (
        <TvPinDialog hasPin={device.has_pin} mac={mac} catName={pinDialog.catName}
          onClose={() => { setPinDialog(null); setTimeout(() => { const id = lastFocus.current; (id && document.querySelector(`[data-testid="${CSS.escape(id)}"]`) || document.querySelector("[data-focusable]"))?.focus(); }, 60); }}
          onSuccess={(pin) => { setSessionPin(pin); setAdultUnlocked(true); const cb = pinDialog.onSuccess; setPinDialog(null); cb && cb(); }} />
      )}

      {/* Fullscreen player overlay */}
      {playing && !playing.nativeCatchup && (
        <div className="fixed inset-0 z-50 bg-black" data-testid="tv-player-overlay" data-modal>
          <TvVideoPlayer url={playing.url} poster={playing.logo} live={playing.kind === "live"}
            resumeFrom={playing.resumeFrom || 0} onBack={closePlayer}
            controlsVisible={playing.kind !== "live" ? cuCtrl : true}
            showFullscreen={playing.kind === "live"}
            forceProxy={playing.kind !== "live"}
            onMode={(m) => reportPlaybackMode(m, playing.name)}
            onProgress={(p, d) => saveProgress(playing, p, d)} />
        </div>
      )}

      {/* Episodes dialog */}
      {episodes && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-8" data-modal>
          <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-zinc-800/60 flex items-center gap-3">
              <button data-testid="ep-back" data-focusable onClick={() => setEpisodes(null)} className="px-4 py-2 rounded-lg bg-zinc-800 text-white flex items-center gap-2"><ArrowLeft className="w-5 h-5" /> Back</button>
              <h3 className="font-head text-2xl font-semibold truncate">{activeSeries?.name}</h3>
            </div>
            <div className="overflow-y-auto p-5 space-y-2 tv-scroll">
              {episodes === "loading" ? <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>
                : episodes.map((ep) => (
                  <button key={ep.id} data-testid={`tv-ep-${ep.id}`} data-focusable
                    onClick={() => { setEpisodes(null); openPlayer({ id: ep.id, name: ep.title, url: ep.url, logo: activeSeries?.logo, type: "episode" }); }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-zinc-900 text-left text-lg">
                    <Play className="w-6 h-6 text-red-500 shrink-0" /> S{ep.season} · {ep.title}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* App update overlay */}
      {update && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col items-center justify-center text-center p-10" data-testid="tv-update-overlay">
          <MonitorPlay className="w-16 h-16 text-red-500 mb-5" />
          <h1 className="font-head text-4xl font-bold">Update available</h1>
          <p className="text-zinc-300 text-xl mt-3">Version {update.version_name}</p>
          {update.message && <p className="text-zinc-400 mt-2 max-w-xl">{update.message}</p>}
          <div className="flex gap-4 mt-8">
            <button data-testid="tv-update-now" data-focusable autoFocus onClick={() => { const u = update.apk_url; if (window.AndroidTV && typeof window.AndroidTV.openUpdateUrl === "function") { window.AndroidTV.openUpdateUrl(u); } else { window.open(u, "_blank"); } }}
              className="px-8 py-4 rounded-xl bg-red-600 text-white text-lg font-semibold">Update now</button>
            {!update.force && <button data-testid="tv-update-later" data-focusable onClick={() => setUpdate(null)}
              className="px-8 py-4 rounded-xl bg-zinc-800 text-white text-lg font-semibold">Later</button>}
          </div>
          {update.force && <p className="text-amber-400 text-sm mt-6">A required update must be installed to continue.</p>}
        </div>
      )}

      {/* Exit message overlay */}
      {showExit && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center text-center p-10" data-modal data-testid="tv-exit-overlay">
          <MonitorPlay className="w-20 h-20 text-red-500 mb-6" />
          <h1 className="font-head text-4xl font-bold">You can now close the app</h1>
          <p className="text-zinc-400 text-xl mt-4 max-w-xl">Press the Home or Back button on your remote to exit, or return to the menu below.</p>
          <button data-testid="tv-exit-back" data-focusable autoFocus onClick={() => setShowExit(false)}
            className="mt-8 px-8 py-4 rounded-xl bg-red-600 text-white text-lg font-semibold flex items-center gap-2"><ArrowLeft className="w-6 h-6" /> Back to Menu</button>
        </div>
      )}
    </div>
  );
}

/* ---- sub views ---- */
function Poster({ item, onEnter, onFav, isFav, wide }) {
  return (
    <button data-testid={`tvcard-${item.id}`} data-focusable onClick={() => onEnter(item)}
      className={`relative overflow-hidden rounded-xl bg-[#121217] border border-zinc-800/50 ${wide ? "aspect-video" : "aspect-[2/3]"}`}>
      <img src={proxied(item.logo) || FALLBACKS[item.type] || FALLBACKS.movie} onError={(e) => { e.target.src = FALLBACKS.movie; }}
        className={`w-full h-full ${wide ? "object-contain p-3 bg-[#0a0a0f]" : "object-cover"}`} alt="" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-3 text-left"><p className="text-sm font-semibold overflow-hidden" style={{ textShadow: "0 1px 3px #000" }}><span className={`tvname ${(item.name || "").length > 18 ? "long" : ""}`}>{item.name}</span></p></div>
      {isFav && <Star className="absolute top-2 right-2 w-5 h-5 fill-red-500 text-red-500" />}
    </button>
  );
}

function TvBrowse({ title, items, cats, activeCat, setActiveCat, loading, onEnter, favIds, onBack, requestUnlock }) {
  const [focusItem, setFocusItem] = useState(null);
  const groupBtn = (label, val, adult) => (
    <button key={val} data-testid={`tvcat-${val}`} data-focusable onClick={() => adult && requestUnlock ? requestUnlock(val, () => setActiveCat(val)) : setActiveCat(val)}
      className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg text-left text-base font-semibold whitespace-nowrap ${activeCat === val ? "bg-red-600 text-white" : "bg-zinc-900/60 text-zinc-300"}`}>
      {adult && <Lock className="w-4 h-4 text-amber-400 shrink-0" />}
      <span className="truncate flex-1">{label}</span>
    </button>
  );
  const preview = focusItem || items[0] || null;
  return (
    <div className="h-full flex">
      {/* Column 1: Groups */}
      <div className="w-64 shrink-0 flex flex-col border-r border-zinc-800/60 p-4">
        <div className="flex items-center gap-3 mb-4">
          <button data-testid="section-back" data-focusable onClick={onBack} className="p-3 rounded-lg bg-zinc-800 text-white shrink-0"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="font-head text-2xl font-bold truncate">{title}</h1>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 tv-scroll pr-1">
          {groupBtn("All", "all", false)}
          {cats.map((c) => groupBtn(`${c.name} (${c.count})`, c.name, c.adult))}
        </div>
      </div>
      {/* Column 2: Vertical item list */}
      <div className="w-[40%] max-w-lg flex flex-col border-r border-zinc-800/60 p-3">
        <div className="flex-1 overflow-y-auto space-y-1.5 tv-scroll">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>
            : items.length === 0 ? <p className="text-zinc-500 py-10 text-base px-2">No results</p>
            : items.map((it) => (
              <button key={it.id} data-testid={`tvcard-${it.id}`} data-focusable onFocus={() => setFocusItem(it)} onClick={() => onEnter(it)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left bg-zinc-900/50 border border-transparent">
                <img src={proxied(it.logo) || FALLBACKS[it.type] || FALLBACKS.movie} onError={(e) => { e.target.src = FALLBACKS.movie; }} className={`shrink-0 rounded bg-[#0a0a0f] ${it.type === "live" ? "w-12 h-8 object-contain" : "w-9 object-cover"}`} style={it.type === "live" ? undefined : { height: "3.25rem" }} alt="" />
                <span className={`tvname text-sm font-medium flex-1 ${(it.name || "").length > 24 ? "long" : ""}`}>{it.name}</span>
                {favIds.includes(it.id) && <Star className="w-4 h-4 fill-red-500 text-red-500 shrink-0" />}
              </button>
            ))}
        </div>
      </div>
      {/* Column 3: Preview */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
        {preview ? (
          <div className="text-center max-w-[16rem]">
            <img src={proxied(preview.logo) || FALLBACKS[preview.type] || FALLBACKS.movie} onError={(e) => { e.target.src = FALLBACKS.movie; }}
              className={`mx-auto rounded-2xl border border-zinc-800/60 ${preview.type === "live" ? "w-44 h-28 object-contain bg-[#0a0a0f] p-4" : "w-full aspect-[2/3] object-cover"}`} alt="" />
            <p className="font-head text-2xl font-semibold mt-5 line-clamp-2">{preview.name}</p>
            <p className="text-zinc-500 mt-4">Press OK to {preview.type === "series" ? "view episodes" : title === "Series" ? "view episodes" : "play"}</p>
          </div>
        ) : <div className="text-zinc-600 flex flex-col items-center gap-3"><Film className="w-14 h-14" /><p className="text-lg">No selection</p></div>}
      </div>
    </div>
  );
}

function TvLive({ items, cats, activeCat, setActiveCat, loading, selected, onActivate, epg, fsLive, showInfo, favIds, onFav, liveQuery, setLiveQuery, showRecent, setShowRecent, recent = [], onCatchup, requestUnlock, livePaused = false, onMode }) {
  const fmt = (s) => s ? s.split(" ")[1]?.slice(0, 5) : "";
  const searching = (liveQuery || "").trim().length >= 1;
  const [editing, setEditing] = useState(false);
  const epgScrollRef = useRef(null);

  // Initial focus on entering Live TV → last selected channel, else the first group.
  // Never the search field (so the on-screen keyboard does not auto-open).
  useEffect(() => {
    const t = setTimeout(() => {
      const sel = selected?.id && document.querySelector(`[data-testid="tvchan-${CSS.escape(selected.id)}"]`);
      (sel || document.querySelector('[data-testid="tvcat-all"]') || document.querySelector('[data-testid^="tvchan-"]'))?.focus();
    }, 150);
    return () => clearTimeout(t);
  }, []);

  // Auto-scroll the guide to the NOW program when EPG loads.
  useEffect(() => {
    if (!epg.length) return;
    const t = setTimeout(() => { epgScrollRef.current?.querySelector('[data-epg-now="1"]')?.scrollIntoView({ block: "center" }); }, 60);
    return () => clearTimeout(t);
  }, [epg]);

  const closeSearch = (focusSel) => {
    setEditing(false);
    setTimeout(() => { (focusSel ? document.querySelector(focusSel) : document.querySelector('[data-testid="tvlive-search"]'))?.focus(); }, 50);
  };
  const channelList = showRecent
    ? recent.map((r) => ({ id: r.content_id, name: r.name, logo: r.logo, url: r.url, category: r.category }))
    : items;
  const showCategory = searching || showRecent;
  const pickGroup = (val) => { setShowRecent(false); setLiveQuery(""); setActiveCat(val); };
  const groupBtn = (label, val, adult) => (
    <button key={val} data-testid={`tvcat-${val}`} data-focusable onClick={() => adult ? requestUnlock(val, () => pickGroup(val)) : pickGroup(val)}
      className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg text-left text-base font-semibold whitespace-nowrap ${!showRecent && !searching && activeCat === val ? "bg-red-600 text-white" : "bg-zinc-900/60 text-zinc-300"}`}>
      {adult && <Lock className="w-4 h-4 text-amber-400 shrink-0" />}
      <span className="truncate flex-1">{label}</span>
    </button>
  );
  return (
    <div className="h-full flex">
      {/* Column 1: Groups */}
      <div className="w-64 shrink-0 flex flex-col border-r border-zinc-800/60 p-4">
        <h1 className="font-head text-2xl font-bold mb-3 px-1">Live TV</h1>
        {!editing ? (
          <button data-testid="tvlive-search" data-focusable onClick={() => setEditing(true)}
            className="w-full mb-3 px-3 py-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-left flex items-center gap-2 text-zinc-400 focus:ring-2 focus:ring-red-500/60 focus:outline-none">
            <SearchIcon className="w-4 h-4 shrink-0" />
            <span className="truncate flex-1">{liveQuery ? liveQuery : "Search channels…"}</span>
          </button>
        ) : (
          <input data-testid="tvlive-search-input" data-focusable autoFocus value={liveQuery}
            onChange={(e) => { setShowRecent(false); setLiveQuery(e.target.value); }}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); closeSearch('[data-testid^="tvchan-"]'); }
              else if (e.key === "Escape" || (e.key === "Backspace" && !liveQuery)) { e.preventDefault(); closeSearch(); }
            }}
            placeholder="Search channels…" className="w-full mb-3 px-3 py-2.5 rounded-lg bg-zinc-900 border border-red-500/60 text-sm focus:outline-none" />
        )}
        <button data-testid="tvlive-recent" data-focusable onClick={() => { setLiveQuery(""); setShowRecent(true); }}
          className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg text-left text-base font-semibold mb-2 ${showRecent ? "bg-red-600 text-white" : "bg-zinc-900/60 text-zinc-300"}`}>
          <History className="w-4 h-4 shrink-0" /> <span className="flex-1">Recently Watched</span>
        </button>
        <div className="flex-1 overflow-y-auto space-y-2 tv-scroll pr-1">
          {groupBtn("All", "all", false)}
          {cats.map((c) => groupBtn(`${c.name} (${c.count})`, c.name, c.adult))}
        </div>
      </div>
      {/* Column 2: Channels */}
      <div className="w-[34%] max-w-md flex flex-col border-r border-zinc-800/60 p-3">
        {(searching || showRecent) && <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold px-2 pb-2">{showRecent ? "Recently Watched" : `Results for “${liveQuery.trim()}”`}</p>}
        <div className="flex-1 overflow-y-auto space-y-1.5 tv-scroll">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>
            : channelList.length === 0 ? <p className="text-zinc-500 py-10 text-base px-2">{showRecent ? "No recently watched channels yet" : searching ? "No matching channels" : "No channels"}</p>
            : channelList.map((ch) => (
              <button key={ch.id} data-testid={`tvchan-${ch.id}`} data-focusable onClick={() => onActivate(ch)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left ${selected?.id === ch.id ? "bg-red-600/25 border border-red-600/50" : "bg-zinc-900/50 border border-transparent"}`}>
                <img src={proxied(ch.logo) || FALLBACKS.live} onError={(e) => { e.target.src = FALLBACKS.live; }} className="w-9 h-6 object-contain rounded bg-[#0a0a0f] shrink-0" alt="" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{ch.name}</span>
                  {showCategory && ch.category && <span className="block text-xs text-zinc-500 truncate">{ch.category}</span>}
                </span>
              </button>
            ))}
        </div>
      </div>
      {/* Column 3: Player + EPG. In fullscreen the SAME player element expands via CSS (single stream, no remount, no logo splash). */}
      <div className="flex-1 flex flex-col p-6 overflow-hidden">
        {selected ? (
          <>
            <div className={fsLive ? "fixed inset-0 z-[60] bg-black" : "aspect-video w-full max-h-[50vh] relative rounded-xl overflow-hidden"} data-testid={fsLive ? "tv-live-fs" : "tv-live-preview"}>
              {typeof window !== "undefined" && window.AndroidTV ? (
                <div className="w-full h-full bg-black" />
              ) : (
                <TvVideoPlayer url={selected.url} poster="" live controls={false} hideBack onBack={() => {}} paused={livePaused} onMode={onMode} />
              )}
              {fsLive && showInfo && (
                <div className="absolute top-8 left-8 right-8 flex items-center gap-5 p-5 rounded-2xl bg-black/70 backdrop-blur border border-white/10 z-10" data-testid="tv-live-info">
                  <img src={proxied(selected.logo) || FALLBACKS.live} onError={(e) => { e.target.src = FALLBACKS.live; }} className="w-20 h-14 object-contain rounded bg-black/40 shrink-0" alt="" />
                  <div className="min-w-0 flex-1">
                    <p className="text-2xl font-semibold truncate">{selected.name}</p>
                    <p className="text-zinc-300 truncate">{(epg.find((p) => p.now) || {}).title || "No guide available"}</p>
                  </div>
                  <div className="text-zinc-400 text-sm text-right shrink-0 leading-relaxed">▼ Next · ▲ Previous<br />Back to exit</div>
                </div>
              )}
            </div>
            {!fsLive && (
              <>
                <div className="flex items-center gap-3 mt-3">
                  <h3 className="font-head text-xl font-semibold truncate flex-1">{selected.name}</h3>
                  <button data-testid="tvlive-fav" data-focusable onClick={() => onFav(selected)} className="px-3 py-1.5 rounded-lg bg-zinc-800 flex items-center gap-1.5 text-sm shrink-0"><Heart className={`w-4 h-4 ${favIds.includes(selected.id) ? "fill-red-500 text-red-500" : ""}`} /> {favIds.includes(selected.id) ? "Favorited" : "Favorite"}</button>
                </div>
                <p className="text-zinc-600 text-xs mt-1">Press OK again on the channel for fullscreen.</p>
                <div className="mt-3 mb-1 flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 font-semibold"><Calendar className="w-3.5 h-3.5" /> Program Guide</div>
                <div ref={epgScrollRef} className="flex-1 overflow-y-auto tv-scroll pr-1">
                  {epg.length === 0 ? <p className="text-zinc-600 text-sm py-2">No guide available.</p>
                    : epg.map((p, i) => {
                      const playable = !!p.catchup && !!p.catchup_url;
                      return (
                        <button key={i} data-testid={`epg-row-${i}`} data-focusable data-epg-now={p.now ? "1" : undefined}
                          onClick={() => playable && onCatchup && onCatchup(p)}
                          className={`w-full flex gap-3 items-baseline px-2 py-1.5 rounded text-left ${p.now ? "bg-red-600/10" : ""} ${p.future ? "opacity-50" : ""}`}>
                          <span className="font-mono-data text-xs text-red-500 w-24 shrink-0">{fmt(p.start)} ~ {fmt(p.end)}</span>
                          <span className={`text-xs truncate flex-1 ${p.now ? "text-white font-medium" : "text-zinc-400"}`}>{p.title}</span>
                          {p.now && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white shrink-0">NOW</span>}
                          {playable && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-600 text-white shrink-0 flex items-center gap-1"><History className="w-2.5 h-2.5" /> CATCH UP</span>}
                        </button>
                      );
                    })}
                </div>
              </>
            )}
          </>
        ) : <div className="flex-1 flex flex-col items-center justify-center text-zinc-600 gap-3"><Tv className="w-16 h-16" /><p className="text-xl">Select a channel</p></div>}
      </div>
    </div>
  );
}

function TvSearch({ search, setSearch, items, onEnter, favIds }) {
  return (
    <div className="h-full flex flex-col px-8 pb-8 pt-4">
      <input data-testid="tv-search-input" data-focusable value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search TV, movies and series..." className="w-full max-w-2xl px-6 py-4 rounded-xl bg-zinc-900 border border-zinc-800 text-xl" />
      <div className="flex-1 overflow-y-auto mt-6 tv-scroll">
        {search.trim().length < 2 ? <p className="text-zinc-600 text-lg">Type at least 2 characters.</p>
          : items.length === 0 ? <p className="text-zinc-500 text-lg">No results</p>
          : <div className="grid grid-cols-4 lg:grid-cols-6 gap-5">{items.map((it) => <Poster key={it.id} item={it} onEnter={onEnter} isFav={favIds.includes(it.id)} wide={it.type === "live"} />)}</div>}
      </div>
    </div>
  );
}

function TvMenu({ device, continueItems, loading, onGo, onReload, onExit, onResume }) {
  const pl = device.playlists?.find((p) => p.id === device.active_playlist) || device.playlists?.[0];
  const c = pl?.counts || {};
  const licenseText =
  device.license_type === "lifetime"
    ? "Lifetime"
    : device.activated
      ? `License: ${device.days_left} day${device.days_left === 1 ? "" : "s"} left`
      : `Trial: ${device.days_left} day${device.days_left === 1 ? "" : "s"} left`;
  const tiles = [
    { key: "live", label: "Live TV", icon: Tv, n: c.live || 0 },
    { key: "movie", label: "Movies", icon: Film, n: c.movie || 0 },
    { key: "series", label: "Series", icon: Clapperboard, n: c.series || 0 },
    { key: "search", label: "Search", icon: SearchIcon },
    { key: "favorites", label: "Favorites", icon: Heart },
    { key: "settings", label: "Settings", icon: SettingsIcon },
  ];
  const actions = [
    { key: "playlist", label: "Playlist", icon: MonitorPlay, onClick: () => onGo("playlist") },
    { key: "reload", label: "Reload", icon: RotateCcw, onClick: onReload },
    { key: "exit", label: "Exit", icon: ArrowLeft, onClick: onExit },
  ];
  return (
    <div className="h-full overflow-y-auto p-10 tv-scroll">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-head text-4xl font-bold">IBO Stream</h1>
          <p className="text-zinc-400 text-lg mt-2">{pl ? `Playlist: ${pl.name}` : "No active playlist"}</p>
          <p className={`text-base mt-1 font-semibold ${
            device.license_type === "lifetime"
              ? "text-emerald-400"
              : device.activated
                ? "text-emerald-400"
                : "text-amber-400"
          }`}>
            {licenseText}
          </p>
        </div>
        <div className="flex gap-3">
          {actions.map(({ key, label, icon: Icon, onClick }) => (
            <button key={key} data-testid={`tvmenu-${key}`} data-focusable onClick={onClick}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#121217] border border-zinc-800/60 text-base font-semibold">
              {key === "reload" && loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5 text-red-500" />} {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-6">
        {tiles.map(({ key, label, icon: Icon, n }) => (
          <button key={key} data-testid={`tvmenu-${key}`} data-focusable onClick={() => onGo(key)}
            className="text-left p-8 rounded-2xl bg-[#121217] border border-zinc-800/50">
            <Icon className="w-10 h-10 text-red-500" />
            {n !== undefined && <p className="font-head text-4xl font-bold mt-4">{n}</p>}
            <p className={`text-zinc-300 text-lg ${n !== undefined ? "" : "mt-4"}`}>{label}</p>
          </button>
        ))}
      </div>
      {continueItems.length > 0 && (
        <div className="mt-12">
          <h2 className="font-head text-2xl font-bold mb-5">Continue Watching</h2>
          <div className="grid grid-cols-4 lg:grid-cols-6 gap-5">
            {continueItems.map((it) => {
              const pct = it.duration > 0 ? Math.min(100, (it.position / it.duration) * 100) : 0;
              return (
                <button key={it.content_id} data-testid={`tvcont-${it.content_id}`} data-focusable onClick={() => onResume(it)}
                  className="relative overflow-hidden rounded-xl bg-[#121217] border border-zinc-800/50 aspect-[2/3]">
                  <img src={proxied(it.logo) || FALLBACKS.movie} onError={(e) => { e.target.src = FALLBACKS.movie; }} className="w-full h-full object-cover" alt="" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-left"><p className="text-sm font-semibold line-clamp-2">{it.name}</p><div className="mt-1.5 h-1.5 rounded-full bg-white/20"><div className="h-full bg-red-600 rounded-full" style={{ width: `${pct}%` }} /></div></div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TvPlaylistPicker({ device, onSwitch, onBack }) {
  const active = device.active_playlist || device.playlists?.[0]?.id;
  return (
    <div className="h-full overflow-y-auto p-10 tv-scroll">
      <div className="flex items-center gap-4 mb-8">
        <button data-testid="tvpl-back" data-focusable onClick={onBack} className="px-5 py-3 rounded-xl bg-zinc-800 text-white flex items-center gap-2 text-base"><ArrowLeft className="w-5 h-5" /> Back</button>
        <h1 className="font-head text-4xl font-bold">Playlists</h1>
      </div>
      {(!device.playlists?.length) ? (
        <p className="text-zinc-500 text-xl">No playlists yet. Add one from the management website.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
          {device.playlists.map((p) => {
            const isActive = p.id === active;
            const cnt = p.counts || {};
            return (
              <button key={p.id} data-testid={`tvpl-${p.id}`} data-focusable onClick={() => onSwitch(p.id)}
                className={`text-left p-6 rounded-2xl border ${isActive ? "bg-red-600/20 border-red-600/60" : "bg-[#121217] border-zinc-800/50"}`}>
                <div className="flex items-center gap-3">
                  <MonitorPlay className={`w-8 h-8 ${isActive ? "text-red-500" : "text-zinc-400"}`} />
                  {isActive && <span className="text-sm px-2 py-1 rounded-md bg-red-600 text-white font-semibold">Active</span>}
                </div>
                <p className="font-head text-2xl font-semibold mt-4 truncate">{p.name}</p>
                <p className="text-zinc-400 text-base mt-2">{cnt.live || 0} Live · {cnt.movie || 0} Movies · {cnt.series || 0} Series</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionWrap({ title, onBack, children }) {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-4 px-8 pt-8">
        <button data-testid="section-back" data-focusable onClick={onBack} className="px-5 py-3 rounded-xl bg-zinc-800 text-white flex items-center gap-2 text-base"><ArrowLeft className="w-5 h-5" /> Back</button>
        <h1 className="font-head text-3xl font-bold">{title}</h1>
      </div>
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

function TvSettings({ device, mac, onReload, pub }) {
  const [pin, setPin] = useState("");
  const [oldPin, setOldPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const rows = [["MAC Address", device.mac], ["Device Key", device.device_key], ["Status", device.status.toUpperCase()],
    ["Trial ends", new Date(device.trial_expiry).toLocaleDateString("en-GB")], ["Activation ends", device.activation_expiry ? new Date(device.activation_expiry).toLocaleDateString("en-GB") : "—"]];
  const inp = "w-40 text-center tracking-[0.4em] text-lg font-mono-data px-3 py-2.5 rounded-lg bg-zinc-900 border border-zinc-800 focus:outline-none focus:ring-2 focus:ring-red-500/50";
  const savePin = async () => {
    setMsg("");
    if (!/^\d{4}$/.test(pin)) { setMsg("PIN must be 4 digits"); return; }
    setBusy(true);
    try { await api.post(`/device/${mac}/pin`, { pin, old_pin: oldPin || undefined }); setMsg(device.has_pin ? "PIN changed" : "PIN set"); setPin(""); setOldPin(""); onReload && onReload(); }
    catch (e) { setMsg(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const removePin = async () => {
    setMsg(""); setBusy(true);
    try { await api.delete(`/device/${mac}/pin`, { data: { pin: oldPin || undefined } }); setMsg("PIN removed"); setPin(""); setOldPin(""); onReload && onReload(); }
    catch (e) { setMsg(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <div className="h-full overflow-y-auto px-8 pb-8 pt-4 tv-scroll space-y-6">
      <div className="max-w-2xl rounded-2xl bg-[#121217] border border-zinc-800/50 p-8 space-y-4 text-xl">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between border-b border-zinc-800/50 pb-3"><span className="text-zinc-500">{k}</span><span className="font-mono-data">{v}</span></div>)}
        <p className="text-zinc-400 text-base pt-2">To add or change your playlist, open the management page on your phone or computer and sign in with your MAC Address and Device Key.</p>
        <DeviceLinks pub={pub} compact />
      </div>

      <div className="max-w-2xl rounded-2xl bg-[#121217] border border-zinc-800/50 p-8 space-y-5" data-testid="tv-parental-card">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20"><Lock className="w-6 h-6 text-amber-400" /></div>
          <div>
            <h3 className="font-head text-2xl font-semibold">Adult / Parental PIN</h3>
            <p className="text-zinc-500 text-base">Protect adult categories with a 4-digit PIN. {device.has_pin ? "A PIN is currently set." : "No PIN set yet."}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {device.has_pin && (
            <input data-testid="tv-old-pin" data-focusable className={inp} value={oldPin} maxLength={4} inputMode="numeric" placeholder="old PIN"
              onChange={(e) => setOldPin(e.target.value.replace(/\D/g, ""))} />
          )}
          <input data-testid="tv-new-pin" data-focusable className={inp} value={pin} maxLength={4} inputMode="numeric" placeholder={device.has_pin ? "new PIN" : "PIN"}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
          <button data-testid="tv-save-pin" data-focusable disabled={busy} onClick={savePin} className="px-6 py-2.5 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-base font-semibold">{device.has_pin ? "Change PIN" : "Set PIN"}</button>
          {device.has_pin && <button data-testid="tv-remove-pin" data-focusable disabled={busy} onClick={removePin} className="px-6 py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-base font-semibold">Remove PIN</button>}
        </div>
        {msg && <p data-testid="tv-pin-msg" className="text-base text-amber-300">{msg}</p>}
      </div>
    </div>
  );
}

function TvPinDialog({ hasPin, mac, catName, onClose, onSuccess }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [step, setStep] = useState(hasPin ? "enter" : "set"); // set -> confirm
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const cur = step === "confirm" ? confirm : pin;
  const setCur = step === "confirm" ? setConfirm : setPin;

  const doVerify = async (value) => {
    setBusy(true); setErr("");
    try { await api.post(`/device/${mac}/pin/verify`, { pin: value }); onSuccess(value); }
    catch { setErr("Incorrect PIN"); setPin(""); }
    finally { setBusy(false); }
  };
  const doSet = async (value) => {
    setBusy(true); setErr("");
    try { await api.post(`/device/${mac}/pin`, { pin: value }); onSuccess(value); }
    catch (e) { setErr(e.response?.data?.detail || "Error"); setStep("set"); setPin(""); setConfirm(""); }
    finally { setBusy(false); }
  };

  // Auto-advance / auto-submit when 4 digits are entered.
  useEffect(() => {
    if (busy) return;
    if (hasPin) { if (pin.length === 4) doVerify(pin); return; }
    if (step === "set" && pin.length === 4) { setStep("confirm"); return; }
    if (step === "confirm" && confirm.length === 4) {
      if (confirm === pin) doSet(pin);
      else { setErr("PINs do not match"); setConfirm(""); setStep("set"); setPin(""); }
    }
  }, [pin, confirm, step]); // eslint-disable-line

  // Hardware number keys / backspace (capture before the global D-pad handler).
  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); e.stopPropagation(); setCur((v) => v.length < 4 ? v + e.key : v); }
      else if (e.key === "Backspace") { e.preventDefault(); e.stopPropagation(); setCur((v) => v.slice(0, -1)); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [step]); // eslint-disable-line

  const title = hasPin ? "Enter PIN" : step === "confirm" ? "Confirm PIN" : "Set a new PIN";
  const sub = hasPin ? `“${catName}” is locked. Enter your 4-digit PIN.` : step === "confirm" ? "Re-enter the PIN to confirm." : "No Adult PIN yet. Create a 4-digit PIN to lock adult content.";
  const dots = [0, 1, 2, 3].map((i) => (
    <span key={i} className={`w-5 h-5 rounded-full border-2 ${i < cur.length ? "bg-red-500 border-red-500" : "border-zinc-600"}`} />
  ));
  const keypad = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"];

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-modal data-testid="tv-pin-dialog" onClick={onClose}>
      <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-sm p-7 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-amber-500/20"><Lock className="w-5 h-5 text-amber-400" /></div>
          <h3 className="font-head text-xl font-semibold">{title}</h3>
        </div>
        <p className="text-sm text-zinc-500">{sub}</p>
        <div className="flex justify-center gap-3 py-2" data-testid="tv-pin-dots">{dots}</div>
        {err && <p className="text-sm text-red-500 text-center" data-testid="tv-pin-error">{err}</p>}
        <div className="grid grid-cols-3 gap-2.5">
          {keypad.map((k) => (
            <button key={k} data-testid={`tv-pin-key-${k}`} data-focusable disabled={busy}
              onClick={() => { setErr(""); if (k === "clear") setCur(""); else if (k === "back") setCur((v) => v.slice(0, -1)); else setCur((v) => v.length < 4 ? v + k : v); }}
              className={`py-4 rounded-xl text-xl font-semibold ${k === "clear" || k === "back" ? "bg-zinc-800 text-zinc-300 text-sm" : "bg-zinc-900 hover:bg-zinc-800 text-white"}`}>
              {k === "clear" ? "Clear" : k === "back" ? "⌫" : k}
            </button>
          ))}
        </div>
        <button data-testid="tv-pin-cancel" data-focusable onClick={onClose} className="w-full py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold">Cancel</button>
      </div>
    </div>
  );
}

function NoPlaylist({ mac, deviceKey, pub }) {
  return (
    <div className="h-full overflow-y-auto flex flex-col items-center justify-center text-center p-10 tv-scroll">
      <MonitorPlay className="w-16 h-16 text-red-500 mb-5" />
      <h1 className="font-head text-4xl font-bold">No playlist yet</h1>
      <p className="text-zinc-400 text-xl mt-4 max-w-xl">On your phone or computer, open the management page and sign in with the codes below to add your playlist.</p>
      <div className="mt-6 glass rounded-2xl p-6 text-2xl font-mono-data">
        <p>MAC: <span className="text-red-500">{mac}</span></p>
        <p className="mt-2">Key: <span className="text-red-500">{deviceKey}</span></p>
      </div>
      <DeviceLinks pub={pub} compact />
    </div>
  );
}

function ActivationScreen({ device, pub }) {
  const [reviewCode, setReviewCode] = useState("");
  const [reviewMsg, setReviewMsg] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);

  const activateReview = async () => {
    if (!reviewCode.trim() || reviewBusy) return;

    setReviewBusy(true);
    setReviewMsg("");

    try {
      await api.post(`/device/${device.mac}/review-activate`, {
        code: reviewCode.trim()
      });

      setReviewMsg("Review access activated. Restarting...");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setReviewMsg(
        e?.response?.data?.detail || "Invalid review code"
      );
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-y-auto flex flex-col items-center justify-center bg-[#050505] text-center p-10 tv-root tv-scroll">
      <div className="p-4 rounded-2xl bg-red-600/20 mb-5"><Lock className="w-14 h-14 text-red-500" /></div>
      <h1 className="font-head text-4xl md:text-5xl font-bold">Trial expired</h1>
      <p className="text-zinc-400 text-xl mt-4 max-w-xl">Your free trial has ended. Activate your device to keep watching. Scan a code below on your phone.</p>
      <div className="mt-6 glass rounded-2xl p-6 text-2xl font-mono-data">
        <p>MAC: <span className="text-red-500">{device.mac}</span></p>
        <p className="mt-2">Device Key: <span className="text-red-500">{device.device_key}</span></p>
      </div>
      <p className="text-zinc-500 mt-4 text-lg">Status: {device.status.toUpperCase()}</p>
      <div className="mt-6 flex gap-4 flex-wrap justify-center">
        <button
          data-focusable
          onClick={() => window.AndroidTV?.buyYear?.()}
          className="px-8 py-4 rounded-xl bg-red-600 text-white text-lg font-semibold"
        >
          Activate 1 Year
        </button>

        <button
          data-focusable
          onClick={() => window.AndroidTV?.buyLifetime?.()}
          className="px-8 py-4 rounded-xl bg-zinc-800 text-white text-lg font-semibold"
        >
          Activate Lifetime
        </button>
      </div>
<div className="mt-6 w-full max-w-md">
  <p className="text-zinc-400 text-lg mb-2">Google Play Review Access</p>

  <div className="flex gap-3">
    <input
      data-focusable
      value={reviewCode}
      onChange={(e) => setReviewCode(e.target.value)}
      placeholder="Review code"
      className="flex-1 px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-700 text-white text-lg outline-none focus:border-red-500"
    />

    <button
      data-focusable
      onClick={activateReview}
      disabled={reviewBusy}
      className="px-5 py-3 rounded-xl bg-zinc-800 text-white text-lg font-semibold disabled:opacity-50"
    >
      {reviewBusy ? "Checking..." : "Activate"}
    </button>
  </div>

  {reviewMsg && (
    <p className="mt-2 text-sm text-zinc-400">{reviewMsg}</p>
  )}
</div>
      <DeviceLinks pub={pub} compact />
    </div>
  );
}
